import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Col,
  Row,
  Typography,
  Form,
  Radio,
  Input,
  Checkbox,
  Skeleton,
  FormInstance,
  Alert,
  Select,
} from 'antd';

import {
  useAddressQuery,
  useDebounce,
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';

import RadioGroup from '@/components/_common/RadioGroup';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import NaturalPersonFields from './NaturalPersonFields';
import LegalPersonFields from './LegalPersonFields';
import { InputDecimal, ZipFormField } from '@/components/_common';
import {
  CustomerFormValues,
  GenderType,
  PersonTypeValid,
} from '@/core/domain/people/Customers/Customers.types';
import { useImportedCpfCnpj } from '@/core/domain/people/ImportedCPFCNPJ/ImportedCPFCNPJ.hook';
import { validateCnpj } from '@/utils/functions/validateCnpj';
import validateCpf from '@/utils/functions/validateCpf';
import moment from 'moment';
import RecommendationSelect from '@/components/_common/Recommendation/RecommendationSelect';
import { useCustomerCpfCnpjExists } from '@/core/domain/people/Customers/Customers.hooks';
import { useCustomerFormContext } from '../CustomerFormProvider';
import { isAppPortocar } from '@/index';
import { isEmpty } from 'lodash';
import CustomerGeneralContactsCreate from '../CustomerGeneralContactsCreate';
import { CustomerContact } from '@/core/domain/people/CustomerContacts/CustomerContacts.types';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import useValidateNoWhiteSpace from '@/hooks/useValidateNoWhiteSpace';
import CountrySelectField from '@/components/_common/CountrySelectField';
import { Country } from '@/types/Country';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';

const { Title } = Typography;

type Props = {
  form: FormInstance<CustomerFormValues>;
  personType?: PersonTypeValid;
  showTitle?: boolean;
  remind?: boolean;
  reminderLoad?: boolean;
  setReminderShow?: () => void;
  isEdit?: boolean;
  contacts?: CustomerContact[];
  setContacts?: React.Dispatch<React.SetStateAction<CustomerContact[]>>;
};

const CustomerGeneralInfoForm: React.FC<Props> = ({
  form,
  showTitle = true,
  remind,
  reminderLoad,
  setReminderShow,
  isEdit = false,
  contacts,
  setContacts,
}) => {
  const intl = useIntl();

  const matrix = useMatrix();
  const shouldFilterMichelin = matrix.data?.matrixSettings.michelinIntegration;

  const { customer, customerDetailsLoading } = useCustomerFormContext();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const formValues = Form.useWatch([], form);
  const personTypeWatch = Form.useWatch('personType', form);
  const enableLGPD = Form.useWatch('enableLGPD', form);
  const cpfCnpj = Form.useWatch('cpfCnpj', form);
  const debounce = useDebounce(cpfCnpj);

  const [disabled, setDisabled] = useState(true);

  const { mutateAsync } = useImportedCpfCnpj();
  const customerCpfCnpjExists = useCustomerCpfCnpjExists();

  const { state, city, zipCode } = useAddressQuery(!isEdit);

  const zipCodeInput = Form.useWatch(['address', 'zipCode'], form);
  const foreignDocument = Form.useWatch('foreignDocument', form);
  const countryOrigin = Form.useWatch('countryOrigin', form);

  useEffect(() => {
    if (foreignDocument && countryOrigin === Country.Brasil) {
      form.setFieldValue('countryOrigin', undefined);
    }
  }, [foreignDocument]);

  useEffect(() => {
    if (zipCode.data && !isEmpty(zipCode.data)) {
      state.setSelectedStateCode(zipCode.data.state);

      setDisabled(false);

      const address = form.getFieldValue('address');

      form.setFieldsValue({
        ...form.getFieldsValue(),
        address: {
          cityId: zipCode.data.cityId,
          street: !!zipCode.data.street ? zipCode.data.street : address?.street,
          district: !!zipCode.data.district
            ? zipCode.data.district
            : address?.district,
          state: zipCode.data.state,
          city: zipCode.data.cityName,
        },
      });
    }
  }, [zipCode.data]);

  useEffect(() => {
    if (zipCodeInput && zipCodeInput.length < 8) {
      setDisabled(true);
      form.setFieldsValue({
        address: undefined,
      });
    }
  }, [zipCodeInput]);

  const handleCpfCnpj = async (cpfCnpjValue: string | undefined) => {
    const cpfCnpjWithoutMask = removeCpfCnpjMask(cpfCnpjValue);
    const isValidCnpj = validateCnpj(cpfCnpjWithoutMask);
    const isValidCpf = validateCpf(cpfCnpjWithoutMask);

    if (cpfCnpjWithoutMask) {
      await customerCpfCnpjExists.mutateAsync(cpfCnpjWithoutMask);
    }

    if (isEdit) return;

    if (isValidCnpj || isValidCpf) {
      const data = await mutateAsync(cpfCnpjWithoutMask);

      form.setFieldsValue({
        name: (isValidCpf ? data.name : data.officialName) || formValues.name,
        birthDate: data.birthDate
          ? moment(data.birthDate)
          : formValues.birthDate,
        nationality: data.birthCountry || formValues.nationality,
        genderType:
          data.gender === 'M'
            ? GenderType.Man
            : data.gender === 'F'
            ? GenderType.Woman
            : formValues.genderType,
        fantasyName: data.tradeName || formValues.fantasyName,
        stateRegistration:
          data.stateRegistration || formValues.stateRegistration,
      });
    }
  };

  useEffect(() => {
    handleCpfCnpj(debounce);
  }, [debounce]);

  const handleType = () => {
    form.resetFields([
      'cpfCnpj',
      'name',
      'birthDate',
      'genderType',
      'nationality',
      'fantasyName',
      'stateRegistration',
    ]);
  };

  useEffect(() => {
    if (!isEdit) {
      handleType();
    }
  }, [personTypeWatch, isEdit]);

  // eslint-disable-next-line
  const handleFormValuesChange = (valuesChanged: any) => {
    handleZipChange(valuesChanged);
    handleStateChange(valuesChanged);
  };

  // eslint-disable-next-line
  const handleZipChange = (valuesChanged: any) => {
    if (!valuesChanged?.address?.zipCode) return;

    const { zipCode: search } = valuesChanged.address;
    zipCode.setSelectedZipCode(undefined);

    if (!search || search.length !== 8) return;

    zipCode.setSelectedZipCode(search);
  };

  // eslint-disable-next-line
  const handleStateChange = (valuesChanged: any) => {
    if (!valuesChanged?.address?.state) return;

    form.resetFields([['address', 'cityId']]);

    state.setSelectedStateCode(valuesChanged.state);
  };

  return (
    <div>
      {customerCpfCnpjExists.data?.exists &&
        debounce !== customer?.cpfCnpj &&
        debounce !== '' && (
          <Alert
            type="warning"
            description="Já existe um cliente cadastrado com esse CPF/CNPJ."
            style={{ marginBottom: 24 }}
          />
        )}

      {showTitle ? (
        <Title level={3} style={{ marginBottom: '24px' }}>
          <FormattedMessage id="customers.generalInfo" />
        </Title>
      ) : null}

      <Form
        form={form}
        name="customerGeneral"
        layout="vertical"
        onValuesChange={handleFormValuesChange}
      >
        <Skeleton active loading={customerDetailsLoading}>
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item
                noStyle
                name="personType"
                initialValue="NATURAL"
                rules={[defaultEmptyFieldError]}
              >
                <RadioGroup style={{ marginBottom: '8px' }}>
                  <Radio value="NATURAL" disabled={isEdit && isAppPortocar}>
                    <FormattedMessage id="customers.domain.personType.natural" />
                  </Radio>
                  <Radio value="LEGAL" disabled={isEdit && isAppPortocar}>
                    <FormattedMessage id="customers.domain.personType.legal" />
                  </Radio>
                </RadioGroup>
              </Form.Item>
            </Col>

            {personTypeWatch === 'NATURAL' && (
              <NaturalPersonFields isEdit={isEdit} />
            )}

            {personTypeWatch === 'LEGAL' && (
              <LegalPersonFields form={form} isEdit={isEdit} />
            )}

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name="countryOrigin"
                label="País"
                rules={!!foreignDocument ? [defaultEmptyFieldError] : []}
              >
                <CountrySelectField
                  allowClear
                  isForeignDocument={!!foreignDocument}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8}>
              <FloatingFormItem
                name="policyNumber"
                label={intl.formatMessage({
                  id: 'customers.domain.policyNumber',
                })}
                rules={[defaultMaxLenghtFieldError(30)]}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8}>
              <FloatingFormItem
                name="website"
                label={intl.formatMessage({ id: 'customers.domain.website' })}
                rules={[defaultMaxLenghtFieldError(70)]}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'customers.domain.creditLimit',
                })}
                name="creditLimit"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={24} lg={4}>
              <Form.Item
                name="rentalCompany"
                initialValue={false}
                valuePropName="checked"
              >
                <Checkbox style={{ marginTop: '12px' }}>
                  <FormattedMessage id="customers.domain.rentalCompany" />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24} lg={shouldFilterMichelin ? 6 : 12}>
              <FloatingFormItem
                name={['recommendation', 'id']}
                label={intl.formatMessage({
                  id: 'customers.form.recommendation',
                })}
              >
                <RecommendationSelect />
              </FloatingFormItem>
            </Col>

            {shouldFilterMichelin && (
              <Col xs={24} sm={24} md={24} lg={6}>
                <FloatingFormItem
                  name="michelinSegmentCode"
                  label={intl.formatMessage({
                    id: 'customers.domain.michelinSegmentCode',
                  })}
                  rules={[
                    defaultEmptyFieldError,
                    useValidateNoWhiteSpace(),
                    defaultMaxLenghtFieldError(3),
                  ]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24}>
              <FloatingFormItem
                name="observation"
                label={intl.formatMessage({
                  id: 'customers.domain.observation',
                })}
              >
                <Input.TextArea rows={4} />
              </FloatingFormItem>
            </Col>

            {isAppPortocar && !isEdit && (
              <>
                <Col span={24}>
                  <Typography.Title level={4}>Endereço</Typography.Title>
                  <Row gutter={[16, 8]} style={{ marginBottom: 24 }}>
                    <Col span={12} md={4}>
                      <ZipFormField
                        disable={false}
                        fieldName={['address', 'zipCode']}
                        label={intl.formatMessage({
                          id: 'insurers.addresses.form.zipCode',
                        })}
                        required
                      />
                      <Form.Item hidden name={['address', 'main']} />
                    </Col>

                    <Col span={12} md={4}>
                      <FloatingFormItem
                        name={['address', 'state']}
                        label={intl.formatMessage({
                          id: 'insurers.addresses.form.id',
                        })}
                      >
                        <Select
                          disabled
                          allowClear
                          showSearch
                          loading={state.isFetching}
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            (option?.label as string)
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          options={state.list.map((state) => ({
                            value: state.code,
                            label: state.code,
                          }))}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={12} md={8}>
                      <Form.Item hidden name={['address', 'city']} />

                      <FloatingFormItem
                        name={['address', 'cityId']}
                        label={intl.formatMessage({
                          id: 'insurers.addresses.form.cityId',
                        })}
                        getValueFromEvent={(value) => {
                          form.setFieldValue(
                            ['address', 'city'],
                            city.list.find((item) => item.id === value)?.name,
                          );

                          return value;
                        }}
                      >
                        <Select
                          disabled
                          allowClear
                          showSearch
                          loading={city.isFetching}
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            (option?.label as string)
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          options={city.list.map((c) => ({
                            value: c.id,
                            label: c.name,
                          }))}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={12} md={8}>
                      <FloatingFormItem
                        name={['address', 'district']}
                        rules={disabled ? [] : [defaultEmptyFieldError]}
                        label={intl.formatMessage({
                          id: 'insurers.addresses.form.district',
                        })}
                      >
                        <Input disabled={disabled} />
                      </FloatingFormItem>
                    </Col>

                    <Col span={12} md={10}>
                      <FloatingFormItem
                        name={['address', 'street']}
                        rules={disabled ? [] : [defaultEmptyFieldError]}
                        label={intl.formatMessage({
                          id: 'insurers.addresses.form.street',
                        })}
                      >
                        <Input disabled={disabled} />
                      </FloatingFormItem>
                    </Col>

                    <Col span={12} md={4}>
                      <FloatingFormItem
                        name={['address', 'number']}
                        label={intl.formatMessage({
                          id: 'insurers.addresses.form.number',
                        })}
                        rules={[
                          disabled ? {} : defaultEmptyFieldError,
                          {
                            pattern: /^[0-9]/,
                            message: intl.formatMessage({
                              id: 'commons.form.errors.invalidField',
                            }),
                          },
                        ]}
                      >
                        <Input disabled={disabled} />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={10}>
                      <FloatingFormItem
                        name={['address', 'complement']}
                        label={intl.formatMessage({
                          id: 'insurers.addresses.form.complement',
                        })}
                      >
                        <Input disabled={disabled} />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} sm={7}>
                      <Form.Item
                        name={['address', 'delivery']}
                        initialValue={false}
                        valuePropName="checked"
                      >
                        <Checkbox style={{ marginTop: '12px' }}>
                          {intl.formatMessage({
                            id: 'insurers.addresses.form.delivery',
                          })}
                        </Checkbox>
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <FloatingFormItem
                        name={['address', 'description']}
                        label={intl.formatMessage({
                          id: 'insurers.addresses.form.description',
                        })}
                        rules={[defaultMaxLenghtFieldError(100)]}
                      >
                        <Input />
                      </FloatingFormItem>
                    </Col>
                  </Row>
                </Col>

                <Col span={24}>
                  <Form.Item shouldUpdate>
                    {({ getFieldError }) => {
                      const hasError =
                        getFieldError('portoContacts')?.length > 0;

                      return (
                        <CustomerGeneralContactsCreate
                          personType={personTypeWatch}
                          contacts={contacts}
                          setContacts={setContacts}
                          hasError={hasError}
                          clearError={() => form.resetFields(['portoContacts'])}
                        />
                      );
                    }}
                  </Form.Item>

                  <Form.Item name="portoContacts" />
                </Col>
              </>
            )}

            <Col span={24}>
              <Form.Item name="isPublicAgency" noStyle valuePropName="checked">
                <Checkbox>
                  <FormattedMessage id="customers.details.generalInfo.form.publicAgency" />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="retentionISS" noStyle valuePropName="checked">
                <Checkbox>
                  <FormattedMessage id="customers.details.generalInfo.form.issRetention" />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="retentionIRPJorCSLL"
                noStyle
                valuePropName="checked"
              >
                <Checkbox>
                  <FormattedMessage id="customers.details.generalInfo.form.irpjOrCsllRetention" />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Checkbox
                onChange={setReminderShow}
                disabled={reminderLoad}
                checked={remind}
              >
                <FormattedMessage id="customers.details.generalInfo.form.enableAlert" />
              </Checkbox>
            </Col>

            <Col span={24}>
              <Form.Item
                name="enableLGPD"
                label={intl.formatMessage({
                  id: 'customers.details.generalInfo.form.enableLgpd',
                })}
                valuePropName="checked"
                getValueFromEvent={(e: number[]) => {
                  const value = e.at(-1);

                  return typeof value === 'number' ? Boolean(value) : null;
                }}
                style={{ margin: 0 }}
              >
                <Checkbox.Group
                  value={enableLGPD !== null ? [Number(enableLGPD)] : []}
                >
                  <Checkbox value={1}>Sim</Checkbox>
                  <Checkbox value={0}>Não</Checkbox>
                </Checkbox.Group>
              </Form.Item>
            </Col>
          </Row>
        </Skeleton>
      </Form>
    </div>
  );
};

export default CustomerGeneralInfoForm;
